/**
 * MAPanel - M&A thesis and target analysis
 */

import { useState, useCallback } from 'react'
import type { CompanyData, MAThesis } from '../../types'

interface MAPanelProps {
  company: CompanyData | null
}

const initialTheses: MAThesis[] = [
  {
    id: '1',
    name: 'Growth Tech Acquisitions',
    criteria: {
      minRevenue: 100000000,
      maxRevenue: 5000000000,
      minFcfMargin: 0.15,
      sectors: ['Technology', 'Software'],
      regimes: ['growth', 'stable'],
    },
  },
  {
    id: '2',
    name: 'Healthcare Consolidation',
    criteria: {
      minRevenue: 50000000,
      maxRevenue: 2000000000,
      minFcfMargin: 0.10,
      sectors: ['Healthcare'],
      regimes: ['stable', 'transition'],
    },
  },
  {
    id: '3',
    name: 'Industrial Roll-up',
    criteria: {
      minRevenue: 25000000,
      maxRevenue: 500000000,
      minFcfMargin: 0.08,
      sectors: ['Industrials', 'Materials'],
      regimes: ['stable'],
    },
  },
]

const availableSectors = [
  'Technology', 'Software', 'Healthcare', 'Financials', 'Industrials',
  'Materials', 'Consumer Discretionary', 'Consumer Staples', 'Energy',
  'Utilities', 'Real Estate', 'Communications'
]

const availableRegimes = ['growth', 'stable', 'transition', 'distressed']

export function MAPanel({ company }: MAPanelProps) {
  const [theses, setTheses] = useState<MAThesis[]>(initialTheses)
  const [selectedThesis, setSelectedThesis] = useState<MAThesis | null>(theses[0])
  const [targetTicker, setTargetTicker] = useState('')
  const [showNewThesisModal, setShowNewThesisModal] = useState(false)

  // New thesis form state
  const [newThesisName, setNewThesisName] = useState('')
  const [newMinRevenue, setNewMinRevenue] = useState('50')
  const [newMaxRevenue, setNewMaxRevenue] = useState('500')
  const [newMinFcfMargin, setNewMinFcfMargin] = useState('10')
  const [newSectors, setNewSectors] = useState<Set<string>>(new Set())
  const [newRegimes, setNewRegimes] = useState<Set<string>>(new Set(['stable']))

  if (!company) {
    return (
      <div className="flex items-center justify-center h-full text-term-textDim">
        <p>Select a company to perform M&A analysis</p>
      </div>
    )
  }

  const score = calculateScore(company, selectedThesis)

  // Toggle sector selection
  const toggleSector = (sector: string) => {
    setNewSectors(prev => {
      const next = new Set(prev)
      if (next.has(sector)) {
        next.delete(sector)
      } else {
        next.add(sector)
      }
      return next
    })
  }

  // Toggle regime selection
  const toggleRegime = (regime: string) => {
    setNewRegimes(prev => {
      const next = new Set(prev)
      if (next.has(regime)) {
        next.delete(regime)
      } else {
        next.add(regime)
      }
      return next
    })
  }

  // Create new thesis
  const handleCreateThesis = useCallback(() => {
    if (!newThesisName.trim() || newSectors.size === 0 || newRegimes.size === 0) {
      return
    }

    const newThesis: MAThesis = {
      id: `thesis-${Date.now()}`,
      name: newThesisName.trim(),
      criteria: {
        minRevenue: parseFloat(newMinRevenue) * 1000000,
        maxRevenue: parseFloat(newMaxRevenue) * 1000000,
        minFcfMargin: parseFloat(newMinFcfMargin) / 100,
        sectors: Array.from(newSectors),
        regimes: Array.from(newRegimes),
      },
    }

    setTheses(prev => [...prev, newThesis])
    setSelectedThesis(newThesis)
    setShowNewThesisModal(false)

    // Reset form
    setNewThesisName('')
    setNewMinRevenue('50')
    setNewMaxRevenue('500')
    setNewMinFcfMargin('10')
    setNewSectors(new Set())
    setNewRegimes(new Set(['stable']))
  }, [newThesisName, newMinRevenue, newMaxRevenue, newMinFcfMargin, newSectors, newRegimes])

  return (
    <div className="space-y-6">
      {/* New Thesis Modal */}
      {showNewThesisModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="term-box p-6 w-full max-w-lg bg-term-bg">
            <h3 className="text-lg font-semibold text-term-text mb-4">Create New Investment Thesis</h3>

            {/* Thesis Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-term-textDim mb-1">Thesis Name</label>
              <input
                type="text"
                value={newThesisName}
                onChange={(e) => setNewThesisName(e.target.value)}
                placeholder="e.g., Tech Value Plays"
                className="term-input w-full"
              />
            </div>

            {/* Revenue Range */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-term-textDim mb-1">Min Revenue ($M)</label>
                <input
                  type="number"
                  value={newMinRevenue}
                  onChange={(e) => setNewMinRevenue(e.target.value)}
                  className="term-input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-term-textDim mb-1">Max Revenue ($M)</label>
                <input
                  type="number"
                  value={newMaxRevenue}
                  onChange={(e) => setNewMaxRevenue(e.target.value)}
                  className="term-input w-full"
                />
              </div>
            </div>

            {/* Min FCF Margin */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-term-textDim mb-1">Min FCF Margin (%)</label>
              <input
                type="number"
                value={newMinFcfMargin}
                onChange={(e) => setNewMinFcfMargin(e.target.value)}
                className="term-input w-32"
              />
            </div>

            {/* Sectors */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-term-textDim mb-2">Target Sectors</label>
              <div className="flex flex-wrap gap-2">
                {availableSectors.map((sector) => (
                  <button
                    key={sector}
                    onClick={() => toggleSector(sector)}
                    className={`px-2 py-1 text-xs rounded border transition-colors ${
                      newSectors.has(sector)
                        ? 'border-term-primary bg-term-primary/20 text-term-primary'
                        : 'border-term-border text-term-textDim hover:border-term-primary/50'
                    }`}
                  >
                    {sector}
                  </button>
                ))}
              </div>
            </div>

            {/* Regimes */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-term-textDim mb-2">Acceptable Regimes</label>
              <div className="flex flex-wrap gap-2">
                {availableRegimes.map((regime) => (
                  <button
                    key={regime}
                    onClick={() => toggleRegime(regime)}
                    className={`px-2 py-1 text-xs rounded border capitalize transition-colors ${
                      newRegimes.has(regime)
                        ? 'border-term-success bg-term-success/20 text-term-success'
                        : 'border-term-border text-term-textDim hover:border-term-success/50'
                    }`}
                  >
                    {regime}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowNewThesisModal(false)}
                className="term-button"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateThesis}
                disabled={!newThesisName.trim() || newSectors.size === 0 || newRegimes.size === 0}
                className="term-button term-button-primary disabled:opacity-50"
              >
                Create Thesis
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-term-text">
          🎯 M&A Analysis - {company.ticker}
        </h2>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Thesis Selector */}
        <div className="term-box p-4">
          <h3 className="text-sm font-medium text-term-text mb-3">Investment Theses</h3>
          <div className="space-y-2 max-h-64 overflow-auto">
            {theses.map((thesis) => (
              <button
                key={thesis.id}
                onClick={() => setSelectedThesis(thesis)}
                className={`w-full text-left p-3 rounded border transition-colors ${
                  selectedThesis?.id === thesis.id
                    ? 'border-term-primary bg-term-primary/10'
                    : 'border-term-border hover:border-term-primary/50'
                }`}
              >
                <div className="font-medium text-term-text">{thesis.name}</div>
                <div className="text-xs text-term-textDim mt-1">
                  {thesis.criteria.sectors.join(', ')}
                </div>
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowNewThesisModal(true)}
            className="term-button term-button-primary w-full mt-3"
          >
            + New Thesis
          </button>
        </div>

        {/* Target Scoring */}
        <div className="col-span-2 space-y-4">
          {/* Current Target */}
          <div className="term-box p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-term-text">Target Score</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={targetTicker}
                  onChange={(e) => setTargetTicker(e.target.value.toUpperCase())}
                  placeholder="Enter ticker..."
                  className="term-input w-32 text-sm"
                />
                <button className="term-button text-sm">Score</button>
              </div>
            </div>

            {/* Score Display */}
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-4 bg-term-bgDark rounded">
                <div className="text-3xl font-bold" style={{ color: getScoreColor(score.overall) }}>
                  {score.overall}
                </div>
                <div className="text-xs text-term-textDim mt-1">Overall Score</div>
              </div>
              <ScoreCard label="Financial Fit" value={score.financial} />
              <ScoreCard label="Strategic Fit" value={score.strategic} />
              <ScoreCard label="Regime Fit" value={score.regime} />
            </div>

            {/* Recommendation */}
            <div className="mt-4 p-3 bg-term-bgDark rounded flex items-center justify-between">
              <div>
                <span className="text-term-textDim">Recommendation: </span>
                <span className={score.overall >= 70 ? 'text-term-success' : score.overall >= 50 ? 'text-term-warning' : 'text-term-error'}>
                  {score.overall >= 70 ? 'Strong Buy' : score.overall >= 50 ? 'Consider' : 'Pass'}
                </span>
              </div>
              <button className="term-button term-button-primary text-sm">
                Generate Report
              </button>
            </div>
          </div>

          {/* Criteria Breakdown */}
          {selectedThesis && (
            <div className="term-box p-4">
              <h3 className="text-sm font-medium text-term-text mb-3">
                Criteria Breakdown: {selectedThesis.name}
              </h3>
              <div className="space-y-3">
                <CriteriaRow
                  label="Revenue Range"
                  value={`$${(selectedThesis.criteria.minRevenue / 1e6).toFixed(0)}M - $${(selectedThesis.criteria.maxRevenue / 1e9).toFixed(1)}B`}
                  meets={true}
                />
                <CriteriaRow
                  label="Min FCF Margin"
                  value={`${(selectedThesis.criteria.minFcfMargin * 100).toFixed(0)}%`}
                  meets={true}
                />
                <CriteriaRow
                  label="Target Sectors"
                  value={selectedThesis.criteria.sectors.join(', ')}
                  meets={selectedThesis.criteria.sectors.includes(company.sector)}
                />
                <CriteriaRow
                  label="Acceptable Regimes"
                  value={selectedThesis.criteria.regimes.join(', ')}
                  meets={true}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface ScoreCardProps {
  label: string
  value: number
}

function ScoreCard({ label, value }: ScoreCardProps) {
  return (
    <div className="text-center p-4 bg-term-bgDark rounded">
      <div className="text-2xl font-bold" style={{ color: getScoreColor(value) }}>
        {value}
      </div>
      <div className="text-xs text-term-textDim mt-1">{label}</div>
    </div>
  )
}

interface CriteriaRowProps {
  label: string
  value: string
  meets: boolean
}

function CriteriaRow({ label, value, meets }: CriteriaRowProps) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-term-border/50">
      <span className="text-term-textDim">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-term-text">{value}</span>
        <span className={meets ? 'text-term-success' : 'text-term-error'}>
          {meets ? '✓' : '✗'}
        </span>
      </div>
    </div>
  )
}

function getScoreColor(score: number): string {
  if (score >= 70) return '#22c55e' // success
  if (score >= 50) return '#eab308' // warning
  return '#ef4444' // error
}

function calculateScore(company: CompanyData, thesis: MAThesis | null): { overall: number; financial: number; strategic: number; regime: number } {
  if (!thesis) return { overall: 0, financial: 0, strategic: 0, regime: 0 }

  // Simplified scoring logic
  const financial = 75 + Math.random() * 20
  const strategic = thesis.criteria.sectors.includes(company.sector) ? 80 + Math.random() * 15 : 40 + Math.random() * 20
  const regime = 70 + Math.random() * 25

  const overall = Math.round((financial + strategic + regime) / 3)

  return {
    overall,
    financial: Math.round(financial),
    strategic: Math.round(strategic),
    regime: Math.round(regime),
  }
}
